import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { type QuizEntity, QuizSchema } from '@/core/infrastructure/schemas/quiz/QuizSchema.ts'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'
import { QuizVersion } from './QuizVersion.ts'
import { QuizVersionListSchema } from '@/core/infrastructure/schemas/quiz/QuizVersionSchemas.ts'

export class Quiz {
  id: number
  type: QuizType.Values
  readonly versions: QuizVersion[] = []

  constructor(quiz: QuizEntity) {
    this.id = quiz.id
    this.type = quiz.type
    this.versions = EntityValidator.validateAndCreateMany(
      QuizVersionListSchema,
      quiz.versions,
      QuizVersion,
      'QuizVersion'
    )
  }

  // Retourne une copie du quiz
  static cloneQuiz(quiz: Quiz): Quiz {
    const quizData = {
      id: quiz.id,
      type: quiz.type,
      versions: EntityValidator.validateAndCreateMany(
        QuizVersionListSchema,
        quiz.versions,
        QuizVersion,
        'QuizVersion'
      )
    }

    return Quiz.create(quizData)
  }
  /**
   * Retourne la version active du quiz
   */
  get activeVersion(): QuizVersion | null {
    return QuizVersion.findActiveVersion(this.versions)
  }

  /**
   * Retourne la version la plus récente du quiz
   */
  get latestVersion(): QuizVersion | null {
    return QuizVersion.findLatestVersion(this.versions)
  }

  /**
   * Retourne toutes les versions triées par numéro décroissant
   */
  get sortedVersions(): QuizVersion[] {
    return QuizVersion.sortByVersionDescending(this.versions)
  }

  /**
   * Vérifie si le quiz a des versions
   */
  get hasVersions(): boolean {
    return this.versions.length > 0
  }

  /**
   * Vérifie si le quiz a déjà une version brouillon
   */
  get hasDraftVersion(): boolean {
    return this.versions.some((version) => version.isDraft)
  }

  /**
   * Retourne le nombre de versions
   */
  get versionCount(): number {
    return this.versions.length
  }

  /**
   * Trouve une version par son ID
   */
  findVersionById(versionId: number): QuizVersion | null {
    return this.versions.find((version) => version.id === versionId) || null
  }

  /**
   * Trouve une version par son numéro
   */
  findVersionByNumber(versionNumber: number): QuizVersion | null {
    return this.versions.find((version) => version.versionNumber === versionNumber) || null
  }

  public static create(data: unknown): Quiz {
    return EntityValidator.validateAndCreate(QuizSchema, data, Quiz, 'Quiz')
  }
}
